/**
 * Hero picks — the four products the whole store funnels into.
 *
 * Choice overload is the biggest conversion leak on a 80-SKU catalogue:
 * first-time visitors land, scroll a wall of climbers and leave without a
 * decision. This module is the single source of truth for the short list
 * we actively recommend, plus the editorial fields the comparison table
 * needs (use case, space, one-line verdict).
 *
 * Only marketing/positioning copy lives here. Every hard spec (age,
 * material, standards) is read from the canonical fact sheet in
 * `@/lib/product-facts` so nothing is invented.
 */
import { storefrontApiRequest, type ShopifyProduct } from "@/lib/shopify";

export type HeroPick = {
  handle: string;
  /** Finnish display title — raw Shopify titles never leak into the UI. */
  displayTitle: string;
  /** Short "best for" label used as the column badge. */
  bestFor: string;
  /** One-sentence verdict shown under the image. */
  verdict: string;
  /** Everyday use case row in the comparison table. */
  useCase: string;
  /** Space requirement row — the #1 question Finnish apartment buyers ask. */
  space: string;
  /** Longevity row: how many years the product realistically serves. */
  lifespan: string;
  /** Highlighted as "the reason to pick this one". */
  pickIf: string;
  /**
   * Optional Montessori24 merchandising label used to tell near-identical
   * manufacturer models apart. Never replaces the official model name — the
   * manufacturer and model are always shown alongside it.
   */
  retailLabel?: string;
  /** One-word differentiator chip, e.g. "Taittuva", "Sisältää rampin". */
  shortDifferentiator?: string;
};

/** Fixed editorial order — climbing first, then kitchen, then soft play. */
export const HERO_PICKS: readonly HeroPick[] = [
  {
    handle: "montessori-climbing-triangle",
    displayTitle: "Pikler-kiipeilykolmio",
    bestFor: "Ensimmäinen Pikler",
    retailLabel: "Pikler Mini",
    shortDifferentiator: "Pieneen kotiin",
    verdict: "Klassikko, joka sopii lähes jokaiseen kotiin ja jokaiselle ikävaiheelle.",
    useCase: "Päivittäinen kiipeily ja tasapaino",
    space: "Taittuu litteäksi — sopii pieneen asuntoon",
    lifespan: "Noin 4 vuotta aktiivista käyttöä",
    pickIf: "Haluat yhden turvallisen perusvalinnan ilman miettimistä.",
  },
  {
    handle: "2-in-1-montessori-climbing-triangle-with-ramp",
    displayTitle: "Kiipeilykolmio ja liukuramppi",
    bestFor: "Kasvaa pisimpään",
    retailLabel: "Pikler Duo",
    shortDifferentiator: "Sisältää rampin",
    verdict: "Kolmio ja käännettävä ramppi: liukumäki, kiipeilyseinä ja maja samassa.",
    useCase: "Kiipeily, liukuminen ja mielikuvitusleikki",
    space: "Vaatii noin 1,5 × 1 m vapaata lattiaa",
    lifespan: "Noin 5 vuotta — vauvasta esikouluikään",
    pickIf: "Haluat yhden hankinnan, joka riittää kouluikään asti.",
  },
  {
    handle: "2in1-kitchen-tower-table-chair",
    displayTitle: "Muunneltava keittiötorni",
    bestFor: "Arjen itsenäisyys",
    retailLabel: "Torni Flex",
    shortDifferentiator: "Muuntuu pöydäksi",
    verdict: "Nostaa lapsen turvallisesti tiskipöydän tasolle — muuntuu pöydäksi ja tuoliksi.",
    useCase: "Ruoanlaitto, leipominen ja käsienpesu yhdessä",
    space: "Pieni jalanjälki, muuntuu leikkipöydäksi",
    lifespan: "Noin 5 vuotta, kahdessa eri muodossa",
    pickIf: "Lapsi haluaa mukaan keittiöön ja kiipeää tuoleille.",
  },
  {
    handle: "soft-play-5-foam-block-set-corner-climber",
    displayTitle: "Pehmoleikkisetti — Kulmakiipeilijä",
    bestFor: "Pienimmille ja pehmeä",
    shortDifferentiator: "Pehmeä",
    verdict: "Kevyet vaahtomuovipalikat: turvallista kiipeilyä jo ennen kuin lapsi kävelee.",
    useCase: "Konttaus, ensiaskeleet ja rakentelu",
    space: "Palikat pinoutuvat nurkkaan käytön jälkeen",
    lifespan: "Noin 3 vuotta, vauvasta taaperoon",
    pickIf: "Lapsi on alle 2-vuotias tai kaipaat pehmeän vaihtoehdon.",
  },
] as const;

const HERO_PICK_QUERY = `
  query GetHeroPick($handle: String!, $country: CountryCode!, $language: LanguageCode!) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      id title handle vendor
      priceRange { minVariantPrice { amount currencyCode } }
      images(first: 1) { edges { node { url altText } } }
      variants(first: 1) {
        edges {
          node {
            id title
            price { amount currencyCode }
            availableForSale
            selectedOptions { name value }
          }
        }
      }
    }
  }
`;

/**
 * Exact-handle lookup per pick. The search endpoint treats `handle:` as a
 * fuzzy token match, so each product is fetched by its precise handle.
 */
export const heroPicksQueryOptions = {
  queryKey: ["products", "hero-picks"] as const,
  queryFn: async () => {
    try {
      const results = await Promise.all(
        HERO_PICKS.map((p) => storefrontApiRequest(HERO_PICK_QUERY, { handle: p.handle })),
      );
      return results
        .map((res) => res?.data?.product)
        .filter(Boolean)
        .map((node) => ({ node })) as ShopifyProduct[];
    } catch (err) {
      console.error("[hero-picks] fetch failed:", err);
      return [] as ShopifyProduct[];
    }
  },
  staleTime: 60_000,
};
